import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { App } from './App';
import { SentryErrorBoundary } from './components/SentryErrorBoundary';
import './index.css';

// ---------------------------------------------------------------------------
// F-601-FE-01: Sentry initialization
// Clinical content fields must never appear in Sentry reports.
// ---------------------------------------------------------------------------

const CLINICAL_FIELDS = new Set([
  'originalText',
  'translatedText',
  'content',
  'audio',
  'speechText',
  'text',
  'transcript',
  'noteContent',
  'patientSpeech',
  'doctorSpeech',
]);

function stripClinicalContent(obj: unknown, depth = 0): unknown {
  if (depth > 10) return '[DEPTH_LIMIT]';
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map((item) => stripClinicalContent(item, depth + 1));
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    sanitized[key] = CLINICAL_FIELDS.has(key) ? '[REDACTED]' : stripClinicalContent(value, depth + 1);
  }
  return sanitized;
}

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    release: (import.meta.env.VITE_RELEASE as string | undefined) ?? undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    beforeSend(event) {
      if (event.extra) {
        event.extra = stripClinicalContent(event.extra) as typeof event.extra;
      }
      if (event.contexts) {
        event.contexts = stripClinicalContent(event.contexts) as typeof event.contexts;
      }
      if (event.request?.data) {
        event.request.data = stripClinicalContent(event.request.data) as typeof event.request.data;
      }
      return event;
    },
  });
}

// ---------------------------------------------------------------------------
// F-601-FE-02: Render with SentryErrorBoundary wrapping the entire app
// ---------------------------------------------------------------------------

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <SentryErrorBoundary>
      <App />
    </SentryErrorBoundary>
  </StrictMode>,
);
